import { useEffect, useState } from "react";
import { send } from "emailjs-com";
import Api from "../../shared/api/api";
import { trackEvent } from "../helpers/analytics";
import "../styles/EmailForm.css";

const INITIAL_STATE = {
  email: "",
  subject: "",
  content: "",
};

const CONTACT_CONFIG = {
  author: {
    recipientEmail: "author@kennethlpowell.com",
    subjectPrefix: "[Author Contact]",
  },
  avwakin: {
    recipientEmail: "avwakindevelopment@outlook.com",
    subjectPrefix: "[Avwakin Inquiry]",
  },
};

const getErrorMessage = (error) => {
  if (error?.text) {
    return error.text;
  }

  if (error?.message) {
    return error.message;
  }

  return "Something went wrong. Please try again.";
};

export default function EmailForm({ source = "author" }) {
  const [formData, setFormData] = useState(INITIAL_STATE);
  const [emailSettings, setEmailSettings] = useState({
    serviceId: "",
    templateId: "",
    publicKey: "",
  });

  const [isLoadingSettings, setIsLoadingSettings] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [statusMessage, setStatusMessage] = useState("");
  const [statusType, setStatusType] = useState("");

  useEffect(() => {
    let isMounted = true;

    const loadEmailSettings = async () => {
      setIsLoadingSettings(true);

      try {
        const response = await Api.getSettings();
        const settings = response?.settings ?? {};

        if (!isMounted) {
          return;
        }

        setEmailSettings({
          serviceId: settings.EmailService || "",
          templateId: settings.template || "",
          publicKey: settings.EmailPassword || "",
        });
      } catch (error) {
        console.error("Unable to load EmailJS settings:", error);

        if (isMounted) {
          setStatusType("error");
          setStatusMessage(
            "The contact form is temporarily unavailable. Please try again later."
          );
        }
      } finally {
        if (isMounted) {
          setIsLoadingSettings(false);
        }
      }
    };

    loadEmailSettings();

    return () => {
      isMounted = false;
    };
  }, []);

  const handleChange = (event) => {
    const { name, value } = event.target;

    setFormData((currentData) => ({
      ...currentData,
      [name]: value,
    }));

    if (statusMessage) {
      setStatusMessage("");
      setStatusType("");
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const normalizedEmail = formData.email.trim();
    const normalizedSubject = formData.subject.trim();
    const normalizedContent = formData.content.trim();

    if (!normalizedEmail || !normalizedSubject || !normalizedContent) {
      setStatusType("error");
      setStatusMessage("Please complete every field before sending.");
      return;
    }

    const contactConfig =
      CONTACT_CONFIG[source] ?? CONTACT_CONFIG.author;

    const { serviceId, templateId, publicKey } = emailSettings;

    if (!serviceId || !templateId || !publicKey) {
      setStatusType("error");
      setStatusMessage(
        "The contact form is not configured correctly. Please try again later."
      );
      return;
    }

    setIsSubmitting(true);
    setStatusMessage("");
    setStatusType("");

    try {
      await send(
        serviceId,
        templateId,
        {
          reply_to: normalizedEmail,
          to_name: contactConfig.recipientEmail,
          subject: `${contactConfig.subjectPrefix} ${normalizedSubject}`,
          message: normalizedContent,
          is_author: source !== "avwakin",
          is_development: source === "avwakin",
        },
        publicKey
      );

      setFormData(INITIAL_STATE);
      if (source === "avwakin") trackEvent("development_inquiry_submit");
      setStatusType("success");
      setStatusMessage(
        "Thanks! Your message has been sent successfully."
      );
    } catch (error) {
      console.error("Email submission failed:", error);

      setStatusType("error");
      setStatusMessage(getErrorMessage(error));
    } finally {
      setIsSubmitting(false);
    }
  };

  const isFormDisabled = isLoadingSettings || isSubmitting;

  return (
    <form className="emailForm" onSubmit={handleSubmit} noValidate>
      <label htmlFor={`${source}-contact-email`}>Your Email:</label>

      <input
        id={`${source}-contact-email`}
        name="email"
        type="email"
        inputMode="email"
        autoComplete="email"
        value={formData.email}
        onChange={handleChange}
        disabled={isFormDisabled}
        aria-describedby={
          statusMessage ? `${source}-contact-status` : undefined
        }
        required
      />

      <label htmlFor={`${source}-contact-subject`}>Subject:</label>

      <input
        id={`${source}-contact-subject`}
        name="subject"
        type="text"
        value={formData.subject}
        onChange={handleChange}
        disabled={isFormDisabled}
        maxLength={150}
        required
      />

      <label htmlFor={`${source}-contact-content`}>Message:</label>

      <textarea
        id={`${source}-contact-content`}
        name="content"
        value={formData.content}
        onChange={handleChange}
        disabled={isFormDisabled}
        maxLength={5000}
        required
      />

      <button type="submit" disabled={isFormDisabled}>
        {isLoadingSettings
          ? "Loading..."
          : isSubmitting
            ? "Sending..."
            : "Send"}
      </button>

      {statusMessage && (
        <p
          id={`${source}-contact-status`}
          className={`emailStatusMessage ${statusType}`}
          role={statusType === "error" ? "alert" : "status"}
          aria-live="polite"
        >
          {statusMessage}
        </p>
      )}
    </form>
  );
}
